import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { FC, InputHTMLAttributes, Ref, forwardRef } from 'react';
import useToggle from '../../../hooks/useToggle';
import { makeId } from '../../../utilities/idComponent';
import { FieldContainer, FontStyle } from '../FieldContainer';
import styles from './PasswordField.module.css';

export type PasswordFieldProps = {
  boldLabel?: boolean;
  children?: React.ReactNode;
  errorMessage?: string;
  errorMessageId?: string;
  fontStyle?: FontStyle;
  helpText?: string;
  isHorizontal?: boolean;
  label?: string | React.ReactNode;
  pin?: boolean;
  shouldUseMessageColor?: boolean;
  variant?: 'desktop' | 'mobile';
  withBackground?: boolean;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

const PasswordField: FC<PasswordFieldProps> = forwardRef(
  (
    {
      boldLabel,
      children,
      className,
      errorMessage,
      errorMessageId,
      helpText,
      id,
      label,
      shouldUseMessageColor,
      name,
      autoComplete = 'nope',
      fontStyle = FontStyle.default,
      isHorizontal = false,
      pin = false,
      variant = 'desktop',
      withBackground = false,

      ...props
    }: PasswordFieldProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [showPassword, toggleShowPassword] = useToggle();

    const isError = Boolean(errorMessage);
    const getMessageStyle = () => {
      if (isError) {
        return styles.errorInput;
      }
      return '';
    };

    const classes = classNames(className, getMessageStyle(), {
      [styles.inputContainer]: variant === 'desktop',
      ['font-body-MdRegular']: variant === 'desktop',
      [styles.mobile]: variant === 'mobile',
      [styles.error]: isError && variant === 'mobile',
    });

    const inputProps = {
      autoComplete,
      name,
      ...props,
    };

    const inputPasswordComponent = (
      <>
        <input
          type={showPassword ? 'text' : 'password'}
          id={showPassword ? undefined : makeId('ipt', name, id)}
          className={classNames(classes, className)}
          {...inputProps}
          maxLength={pin ? 6 : props.maxLength}
          pattern={pin ? '[0-9]*' : props.pattern}
          inputMode={pin ? 'numeric' : props.inputMode}
          ref={ref}
        />
        {!pin && (
          <div
            role="button"
            onClick={() => toggleShowPassword()}
            className={classNames(
              styles.eyeIcon,
              {
                'is-invalid': Boolean(errorMessage),
                [styles.eyeIconWithBackground]: withBackground,
              },
              'font-body-MdRegular',
            )}
          >
            <SystemIcon
              style={{ userSelect: 'none' }}
              name={showPassword ? 'eye' : 'eye-slash'}
              size={20}
              color={isError ? Colors.RED100 : Colors.BLACK80}
            />
          </div>
        )}
      </>
    );

    return (
      <FieldContainer
        fontStyle={fontStyle}
        boldLabel={boldLabel}
        label={label}
        name={name}
        helpText={helpText}
        isHorizontal={isHorizontal}
        errorMessage={errorMessage}
        errorMessageId={errorMessageId}
        shouldUseMessageColor={shouldUseMessageColor}
      >
        {variant === 'mobile' ? (
          <div className={classNames(styles.container, styles.inputBlack)}>
            {inputPasswordComponent}
          </div>
        ) : (
          <div className={styles.inputBlack}>
            <div
              className={classNames(
                'input-group',
                'font-body-MdRegular',
                styles.fieldContainer,
              )}
            >
              {inputPasswordComponent}
            </div>
            {children}
          </div>
        )}
      </FieldContainer>
    );
  },
);

export default PasswordField;
